import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { TrendingUp, Database } from "lucide-react";

/**
 * Renders the prospect's percentile chip next to their overall score.
 * Three states:
 *   A — match_count >= config_threshold: "Y/100 — Nth percentile among M [size] [industry] in [country]"
 *   B — 1 <= match_count < threshold:    "Benchmark dataset is growing. We will show this prospect's percentile when we have enough comparable assessments."
 *   C — match_count == 0:                renders nothing
 *
 * The chip fetches /api/benchmarks itself; the consumer just supplies the
 * filter tuple + the prospect's score.
 */
const SIZE_LABEL = {
  Small: "small",
  Medium: "mid-size",
  Large: "large",
};

function pluralIndustry(industry) {
  if (!industry) return "";
  // "Manufacturing" -> "manufacturers"
  const lower = industry.toLowerCase();
  if (lower.endsWith("ing")) return lower.replace(/ing$/, "ers"); // manufacturing -> manufacturers
  if (lower.endsWith("services")) return lower; // already plural
  if (lower.endsWith("s")) return lower;
  return lower + " companies";
}

function ordinal(n) {
  const i = Math.round(n);
  const v = i % 100;
  if (v >= 11 && v <= 13) return `${i}th`;
  switch (i % 10) {
    case 1: return `${i}st`;
    case 2: return `${i}nd`;
    case 3: return `${i}rd`;
    default: return `${i}th`;
  }
}

export default function BenchmarkChip({ country, industry, fn, sizeBand, score }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!country || !industry || !fn || score == null) return;
    setLoading(true);
    const params = new URLSearchParams({ country, industry, function: fn, score: String(score) });
    if (sizeBand) params.set("size_band", sizeBand);
    api
      .get(`/benchmarks?${params.toString()}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [country, industry, fn, sizeBand, score]);

  if (loading || !data) return null;

  const { match_count, percentile, config_threshold } = data;

  // State C — nothing comparable, render nothing.
  if (match_count === 0) return null;

  // State B — dataset still growing.
  if (match_count < config_threshold) {
    return (
      <div
        data-testid="benchmark-chip-growing"
        className="mt-3 inline-flex items-start gap-2 text-xs text-muted-foreground border border-dashed border-border/60 rounded-md px-3 py-2 max-w-md leading-relaxed"
      >
        <Database className="w-3.5 h-3.5 mt-0.5 text-accent shrink-0" />
        <span>
          Benchmark dataset is growing. We&rsquo;ll show this prospect&rsquo;s percentile when we have enough comparable Discoveries.
        </span>
      </div>
    );
  }

  // State A — show full percentile.
  const sizeLabel = sizeBand && SIZE_LABEL[sizeBand] ? `${SIZE_LABEL[sizeBand]} ` : "";
  const industryLabel = pluralIndustry(industry);
  const placeLabel = country ? ` in ${country}` : "";
  return (
    <div
      data-testid="benchmark-chip-percentile"
      className="mt-3 inline-flex items-center gap-2 text-xs text-foreground bg-accent/10 border border-accent/30 rounded-full px-3 py-1.5"
    >
      <TrendingUp className="w-3.5 h-3.5 text-accent" />
      <span className="font-medium">
        {score}/100 — {ordinal(percentile)} percentile among {match_count} {sizeLabel}{industryLabel}{placeLabel}
      </span>
    </div>
  );
}
